"use client";

import { createContext, useContext, useEffect, useState } from "react";

import { VisuallyHidden } from "@mantine/core";

import { useDeskMonitor } from "./DeskMonitor";
import classes from "./SessionCount.module.css";
import { createRandom } from "./timeline";

/* One more session every two to six seconds. */
const TICK_MIN_MS = 2000;
const TICK_SPREAD_MS = 4000;
const TICK_SEED = 387;

/** The terminal's status line catches up a beat after the label rolls. */
const TERMINAL_FOLLOW_MS = 250;

interface SessionCountState {
  count: number;
  /** What the count read before its last tick, so a changed digit can roll from it. */
  previousCount: number;
}

const SessionCountContext = createContext<SessionCountState | null>(null);

function useSessionCount(): SessionCountState {
  const state = useContext(SessionCountContext);

  if (!state) {
    throw new Error("A session count renders inside a SessionCountProvider.");
  }

  return state;
}

/**
 * Today's AI sessions, which keep climbing after the rest of the machine
 * monitor settles. The count shows in two places, the monitor's label and the
 * sessions window's status line, so its state lives here, above both. It ticks
 * only while the monitor is playing: on screen, in a visible tab, with motion
 * on. The meetings count and the 23 agents never move.
 */
export function SessionCountProvider({
  sessionsToday,
  children,
}: {
  sessionsToday: number;
  children: React.ReactNode;
}) {
  const { isPlaying } = useDeskMonitor();
  const [random] = useState(() => createRandom(TICK_SEED));
  const [state, setState] = useState<SessionCountState>({
    count: sessionsToday,
    previousCount: sessionsToday,
  });

  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    let timer: number | undefined;

    function scheduleTick() {
      timer = window.setTimeout(
        () => {
          setState(({ count }) => ({ count: count + 1, previousCount: count }));
          scheduleTick();
        },
        TICK_MIN_MS + random() * TICK_SPREAD_MS,
      );
    }

    scheduleTick();

    return () => window.clearTimeout(timer);
  }, [isPlaying, random]);

  return <SessionCountContext.Provider value={state}>{children}</SessionCountContext.Provider>;
}

/**
 * The count on the monitor's label, one clipped slot per digit: a digit that
 * changes dims, then rolls up to its next value. Each roll remounts its slot,
 * so a roll cut short by the next tick never leaves digits stacked in it.
 * Screen readers get the whole number once, from a hidden copy.
 */
export function RollingSessionCount() {
  const { count, previousCount } = useSessionCount();
  const digits = String(count).split("");
  const previousDigits = String(previousCount).split("");
  const canRoll = digits.length === previousDigits.length;

  return (
    <data className={classes.count} value={count}>
      <VisuallyHidden>{count}</VisuallyHidden>
      <span aria-hidden>
        {digits.map((digit, index) => {
          const isRolling = canRoll && digit !== previousDigits[index];

          return (
            <span key={isRolling ? `${index}-${count}` : index} className={classes.slot}>
              {isRolling && <span className={classes.outgoing}>{previousDigits[index]}</span>}
              <span className={isRolling ? classes.incoming : undefined}>{digit}</span>
            </span>
          );
        })}
      </span>
    </data>
  );
}

/** The count in the sessions window's status line, a beat behind the label. */
export function TerminalSessionCount({ className }: { className?: string }) {
  const { count } = useSessionCount();
  const [shownCount, setShownCount] = useState(count);

  useEffect(() => {
    const timer = window.setTimeout(() => setShownCount(count), TERMINAL_FOLLOW_MS);

    return () => window.clearTimeout(timer);
  }, [count]);

  return <b className={className}>{shownCount}</b>;
}
